import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { useGetInvoiceReportPaymentInfo } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { SmartListViewDetails } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/ApplicationContent/components/SmartListViewDetails';
import { useDataChangeReset } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useDataChangeReset';
import { useSetBusinessDocumentsData } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useSetBusinessDocumentsData';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { FC, memo, Suspense, useEffect } from 'react';

type ApplicationDetailProps = {
  reportId: string;
};

type InnerListProps = {
  reportId: string;
};

type ErrorFallbackProps = {
  error: Error | null;
};

const InnerList: FC<InnerListProps> = memo(({ reportId }) => {
  const { data } = useGetInvoiceReportPaymentInfo(reportId);
  useDataChangeReset(data);
  useSetBusinessDocumentsData(data);

  return <SmartListViewDetails data={data ?? { report_form_inputs: [] }} />;
});
InnerList.displayName = 'InnerList';

const ErrorFallback: FC<ErrorFallbackProps> = ({ error }) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  useEffect(() => {
    notify.error({
      title: error?.message ?? t('system_error'),
      duration: 3,
    });
  }, [error, notify, t]);
  return <></>;
};
ErrorFallback.displayName = 'ErrorFallback';

export const ApplicationDetail: FC<ApplicationDetailProps> = memo(
  ({ reportId }) => {
    return (
      <Suspense fallback={<SuspenseLoading />}>
        <ErrorBoundary
          fallback={({ error }) => <ErrorFallback error={error} />}
        >
          <InnerList reportId={reportId} />
        </ErrorBoundary>
      </Suspense>
    );
  }
);

ApplicationDetail.displayName = 'ApplicationDetail';
